import React from 'react';
import styled, { css } from 'styled-components';

import { ColorScheme, ColorConfig } from '../config/colorSchemes';
import { QUERIES } from '../constants/mediaQueries';
import { FormFieldType, FormNameType } from '../constants/formFields';
import Button from './Button';
import { fadeIn, slideUp } from '../styles/AnimationStyles';

const FormStyles = styled.form<{
  $colorScheme: ColorScheme;
  $inline?: boolean;
  $animate?: boolean;
  $width?: string;
}>`
  display: flex;
  flex-direction: ${(props) => (props.$inline ? 'row' : 'column')};
  margin: ${(props) => (props.$inline ? '0' : '0 auto')};
  align-self: center;
  gap: var(--spacing-small);
  width: ${(props) => (props.$inline ? '100%' : props.$width || 'var(--width-container-base)')};

  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p {
    color: ${(props) => ColorConfig[props.$colorScheme].text};
  }

  input:not([type='hidden']),
  textarea {
    font-family: var(--font-family);
    background: ${(props) => ColorConfig[props.$colorScheme].inputBackground};
    border: ${(props) => ColorConfig[props.$colorScheme].inputBorder};
    border-radius: var(--radius-input);
    color: ${(props) => ColorConfig[props.$colorScheme].inputColor};
    padding: var(--spacing-small);
    width: 100%;
    transition: var(--transition-all-ease);

    &:focus {
      border-color: ${(props) => ColorConfig[props.$colorScheme].inputFocusBorder};
      outline: none;
    }

    &::placeholder {
      color: ${(props) => ColorConfig[props.$colorScheme].inputPlaceholder};
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 1000px
        ${(props) => ColorConfig[props.$colorScheme].inputAutofillBackground} inset !important;
      -webkit-text-fill-color: ${(props) =>
        ColorConfig[props.$colorScheme].inputAutofillColor} !important;
      border-color: ${(props) => ColorConfig[props.$colorScheme].inputBorder} !important;
      transition: background-color 5000s ease-in-out 0s;
    }
  }

  textarea {
    min-height: var(--height-text-area);
    height: auto;
    resize: vertical;
  }

  /* Animation styles for contact form */
  ${(props) =>
    props.$animate &&
    css`
      opacity: 0;
      animation:
        ${fadeIn} 0.5s ease-out 0.4s forwards,
        ${slideUp} 0.8s ease-out 0.4s forwards;
      transition: var(--transition-all-ease);
    `}

  button {
    align-self: ${(props) => (props.$inline ? 'flex-start' : 'center')};
    margin-top: ${(props) => (props.$inline ? '0' : 'var(--spacing-large)')};
  }

  .field-group {
    display: flex;
    flex-direction: ${(props) => (props.$inline ? 'row' : 'column')};
    width: 100%;
  }

  ${QUERIES.maxTablet} {
    width: 100%;
  }

  ${QUERIES.maxMobile} {
    flex-direction: column;
    padding: ${(props) => (!props.$inline ? 'var(--spacing-base)' : '0')};
  }
`;
interface FormProps {
  formName: FormNameType;
  fields: FormFieldType[];
  submitButtonText?: string;
  colorScheme?: ColorScheme;
  buttonColourScheme?: ColorScheme;
  inline?: boolean;
  width?: string;
  animate?: boolean;
  className?: string;
}

const Form: React.FC<FormProps> = ({
  formName,
  fields,
  submitButtonText = 'Submit',
  colorScheme = 'scheme1',
  buttonColourScheme,
  inline = false,
  width,
  animate = false,
  className,
}) => {
  return (
    <FormStyles
      id={formName}
      name={formName}
      method="POST"
      action="/form-success"
      data-netlify="true"
      $netlify-honeypot="bot-field"
      $inline={inline}
      $animate={animate}
      $colorScheme={colorScheme}
      $width={width}
      className={className}
    >
      <input className="hidden" name="bot-field" />
      <input type="hidden" name="form-name" value={formName} />
      {fields.map((field) => (
        <div key={field.name} className="field-group">
          <label className="sr-only" htmlFor={`${formName}-${field.name}`}>
            {field.label}
          </label>
          {field.type === 'textarea' ? (
            <textarea
              id={`${formName}-${field.name}`}
              name={field.name}
              placeholder={field.placeholder}
              required={field.required}
              aria-required={field.required}
              autoComplete={field.autocomplete}
            />
          ) : (
            <input
              id={`${formName}-${field.name}`}
              name={field.name}
              type={field.type}
              placeholder={field.placeholder}
              required={field.required}
              aria-required={field.required}
              autoComplete={field.autocomplete}
            />
          )}
        </div>
      ))}
      <Button
        type="submit"
        text={submitButtonText}
        colorScheme={buttonColourScheme || colorScheme}
      />
    </FormStyles>
  );
};

export default Form;
